/**
 * Product Card Color Swatches
 * 
 * Handles interactive color swatch functionality on product cards:
 * - Hover to preview variant image
 * - Click to select variant
 * - Make card content clickable to navigate to selected variant
 */

class CardColorSwatches {
  constructor() {
    this.init();
  }

  /**
   * Initialize the color swatches feature
   * Find all product cards with color swatches and attach event listeners
   */
  init() {
    // Find all product grid containers with color swatches
    const productCards = document.querySelectorAll('.product-grid-container');

    productCards.forEach(card => {
      const swatches = card.querySelectorAll('.card__color-swatch');
      
      if (swatches.length === 0) return;

      // Add has-color-options class to the card
      card.classList.add('has-color-options');

      // Get the product image element
      const cardImage = card.querySelector('.grid__image img');
      if (!cardImage) return;

      // Store original image data
      const originalImageSrc = cardImage.getAttribute('src');
      const originalImageAlt = cardImage.getAttribute('alt');

      // Make card content clickable
      this.makeCardContentClickable(card, swatches);

      // Attach event listeners to each swatch
      swatches.forEach(swatch => {
        // Skip sold-out swatches
        if (swatch.classList.contains('card__color-swatch--soldout')) {
          return;
        }

        // Hover to preview image
        swatch.addEventListener('mouseenter', () => {
          this.handleSwatchHover(swatch, cardImage);
        });

        // Restore original image on mouse leave
        swatch.addEventListener('mouseleave', () => {
          this.restoreOriginalImage(cardImage, swatches, originalImageSrc);
        });

        // Click to select variant
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSwatchClick(swatch, swatches, cardImage, card);
        });
      });
    });
  }

  /**
   * Make the entire card content area clickable
   * Clicking anywhere on the card navigates to the selected variant
   */
  makeCardContentClickable(card, swatches) {
    // Get the active swatch (or first swatch if none active)
    const activeSwatch = card.querySelector('.card__color-swatch--active') || swatches[0];
    
    if (activeSwatch) {
      const variantUrl = activeSwatch.getAttribute('href');
      card.setAttribute('data-product-url', variantUrl);
    }

    // Make all links within the card update to the selected variant URL
    const cardLinks = card.querySelectorAll('a:not(.card__color-swatch)');
    cardLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const currentUrl = card.getAttribute('data-product-url');
        if (currentUrl) {
          link.setAttribute('href', currentUrl);
        }
      });
    });
  }

  /**
   * Handle swatch hover - preview the variant image
   */
  handleSwatchHover(swatch, cardImage) {
    const variantImageSrc = swatch.getAttribute('data-variant-image');
    
    if (variantImageSrc && variantImageSrc !== '') {
      // Fade out current image
      cardImage.style.opacity = '0.5';
      
      // Change image after brief delay for smooth transition
      setTimeout(() => {
        cardImage.setAttribute('src', variantImageSrc);
        cardImage.style.opacity = '1';
      }, 150);
    }
  }

  /**
   * Restore the original image when mouse leaves swatch
   * If a swatch is active, show that variant's image instead
   */
  restoreOriginalImage(cardImage, swatches, originalImageSrc) {
    // Find the active swatch
    const activeSwatch = Array.from(swatches).find(s => 
      s.classList.contains('card__color-swatch--active')
    );

    if (activeSwatch) {
      // Restore to active variant's image
      const activeImageSrc = activeSwatch.getAttribute('data-variant-image');
      if (activeImageSrc && activeImageSrc !== '') {
        cardImage.style.opacity = '0.5';
        setTimeout(() => {
          cardImage.setAttribute('src', activeImageSrc);
          cardImage.style.opacity = '1';
        }, 150);
      }
    } else {
      // Restore to original image
      cardImage.style.opacity = '0.5';
      setTimeout(() => {
        cardImage.setAttribute('src', originalImageSrc);
        cardImage.style.opacity = '1';
      }, 150);
    }
  }

  /**
   * Handle swatch click - select the variant
   * Update active state, image, and card URL
   */
  handleSwatchClick(swatch, swatches, cardImage, card) {
    // Remove active class from all swatches
    swatches.forEach(s => s.classList.remove('card__color-swatch--active'));
    
    // Add active class to clicked swatch
    swatch.classList.add('card__color-swatch--active');

    // Update the product image to the selected variant
    const variantImageSrc = swatch.getAttribute('data-variant-image');
    if (variantImageSrc && variantImageSrc !== '') {
      cardImage.style.opacity = '0.5';
      setTimeout(() => {
        cardImage.setAttribute('src', variantImageSrc);
        cardImage.style.opacity = '1';
      }, 150);
    }

    // Update the card's product URL to the selected variant
    const variantUrl = swatch.getAttribute('href');
    if (variantUrl) {
      card.setAttribute('data-product-url', variantUrl);
      
      // Update all links in the card to point to the selected variant
      const cardLinks = card.querySelectorAll('a:not(.card__color-swatch)');
      cardLinks.forEach(link => {
        link.setAttribute('href', variantUrl);
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardColorSwatches();
  });
} else {
  // DOM already loaded
  new CardColorSwatches();
}

// Re-initialize when Shopify theme editor updates sections
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new CardColorSwatches();
  });
}

